import appstore from "../../appStore";
import { BookingStatus, ApiGeoPoint } from "../../Services/BookingEntities";
import { EstimatePotentialEtaDecrease } from "../MyBookings/ETA/CanDeferEtaRefresh";
import { MinRefreshInterval, MaxRefreshInterval } from "../MyBookings/ETA/Parameters";
import { IsQuotaAvailable, NoteQuotaUsage } from "../MyBookings/ETA/RequestQuota";
import { EtaKind, ExtendedEta } from "../MyBookings/MyBookingEntities";
import { GetCurrentTime } from "../Utils/GetCurrentTime";
import { PerformEtaRefresh } from "./PerformEtaRefreshForVoiceBooking";
import { PhoneToCnpBookingDetails } from "./PhoneToCnpEntities";
import { Duration } from "moment";

/** 
 * Update the ETA of a voice channel booking if suitable for loading ETA and quota available. 
 * Docs: https://cabcharge.atlassian.net/wiki/spaces/CA/pages/912949892/Background+Booking+ETA+Refresh 
 * Returns the ETA loaded from this method call, or null if there is no load. 
  */
export async function ConsiderRefreshingVoiceBookingEta(): Promise<ExtendedEta | null> {

    // quota
    if (IsQuotaAvailable() == false) return null;

    const candidateBooking = appstore.getState().PhoneToCnp.Booking!;

    if (!DoesEtaNeedRefresh(candidateBooking)) return null;

    // go!
    NoteQuotaUsage();
    return await PerformEtaRefresh(candidateBooking);
}


/** Returns true if this booking is available for an ETA refresh.
 *  It must be in the right status, and have a known start and finish point for the ETA. 
 *  Also must not have had an ETA refresh recently. */
function DoesEtaNeedRefresh(booking: PhoneToCnpBookingDetails): boolean {

    // only for accepted / pickedup
    const statusId = booking.TrackingInfo.BookingStatusID;
    if ((statusId != BookingStatus.Accepted) && (statusId != BookingStatus.PickedUp)) return false;

    // vehicle position must be known
    const vehicleLocation = booking.TrackingInfo.VehicleLocation;

    if (!vehicleLocation) return false;

    // these values to check depend on the status
    let refreshInterval: Duration;
    let requiredLocation: ApiGeoPoint | null;

    if (statusId == BookingStatus.Accepted) {

        // Accepted: shorter timer, needs pickup location
        requiredLocation = {
            Latitude: booking.Pickup.Latitude,
            Longitude: booking.Pickup.Longitude
        };
        refreshInterval = MinRefreshInterval.Accepted;
    }
    else {
        // Picked Up: longer timer, needs dropoff location
        requiredLocation = {
            Latitude: booking.DropOff.Latitude,
            Longitude: booking.DropOff.Longitude
        };
        refreshInterval = MinRefreshInterval.PickedUp;
    }

    // common checks for accepted and picked up
    if (!requiredLocation) return false;
    if (IsExistingEtaStillCurrent(booking, refreshInterval)) return false;

    // OK!!!
    return true;
}

/** Returns true if the specified ETA was loaded recently, and thus does not need a refresh. */
function IsExistingEtaStillCurrent(booking: PhoneToCnpBookingDetails, refreshInterval: Duration): boolean {

    // no existing ETA
    const actualEta = GetEffectiveEta(booking);
    if (!actualEta) return false;

    // always refresh at the max limit (e.g. 120s)
    const dataAgeMillis: number = GetCurrentTime().diff(actualEta.TimeOfCalculation);
    if (dataAgeMillis > MaxRefreshInterval.asMilliseconds()) return false;

    // never refresh below the min limit (e.g. 30s)
    if (dataAgeMillis < refreshInterval.asMilliseconds()) return true;

    // don't refresh if car is not really moving
    if (CanDeferEtaRefreshDueToLackOfMovement(booking, refreshInterval, actualEta)) return true;

    // has moved a bit, time has elapsed quite a bit: need refresh
    return false;
}

/** Handles the case where a booking has an ETA, but the ETA was the for the pickup and we now want a dropoff ETA. 
* Effectively, there is no ETA available in this case. */
function GetEffectiveEta(booking: PhoneToCnpBookingDetails): ExtendedEta | null {

    if (!booking.Eta) return null;

    // this is the only direction in which the ETA can be the wrong way around
    if ((booking.TrackingInfo.BookingStatusID == BookingStatus.PickedUp) && (booking.Eta.Kind == EtaKind.ToPickup)) {
        return null;
    }

    return booking.Eta;
}

/** 
 * Returns true if we detect the Taxi has been mostly stationary since the last ETA refresh, and therefore we don't need a refresh. 
 * Docs: https://cabcharge.atlassian.net/wiki/spaces/CA/pages/928973446/Idea%3A+Reduce+ETA+Calls+Based+On+Location+Drift
 */
function CanDeferEtaRefreshDueToLackOfMovement(booking: PhoneToCnpBookingDetails, refreshInterval: Duration, actualEta: ExtendedEta): boolean {

    // location will be defined in this code path; see DoesEtaNeedRefresh
    const taxiCurrentLocation = booking.TrackingInfo.VehicleLocation!;

    const maxPotentialEtaChange = EstimatePotentialEtaDecrease(actualEta, taxiCurrentLocation);
    if (maxPotentialEtaChange.asMilliseconds() < refreshInterval.asMilliseconds()) return true;

    // genuinely large change in ETA; can't defer refresh
    return false;
}